package com.htmlagent.shared.stream

import com.htmlagent.shared.session.SessionUsageResponse
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

/**
 * What travels in the `data:` field of every SSE event.
 *
 * A sealed hierarchy so the frontend's `when` is exhaustive without `else`: adding an event
 * breaks the compilation of backend and UI at once. That requires the JSON to carry the
 * `type` discriminator, which the backend guarantees when serializing.
 */
@Serializable
sealed interface StreamEvent {

    /** A fragment of the agent's prose. */
    @Serializable
    @SerialName("text_delta")
    data class TextDelta(val text: String) : StreamEvent

    /** The model started the page: the UI switches to showing the incoming code. */
    @Serializable
    @SerialName("artifact_start")
    data object ArtifactStart : StreamEvent

    /** A fragment of the HTML. Shown as code; never rendered half-finished. */
    @Serializable
    @SerialName("artifact_delta")
    data class ArtifactDelta(val html: String) : StreamEvent

    /**
     * The page is complete and saved: now the iframe can change. [version] is what URLs point
     * at; [label] is what people read, `3` for a new page and `3.1` for a fix on it.
     */
    @Serializable
    @SerialName("artifact_done")
    data class ArtifactDone(val version: Int, val label: String) : StreamEvent

    /**
     * The agent is using a tool. Only the name and an already validated subject, never the
     * raw arguments, which the model generated.
     */
    @Serializable
    @SerialName("tool_start")
    data class ToolStart(val tool: String, val subject: String? = null) : StreamEvent

    /** The tool finished. */
    @Serializable
    @SerialName("tool_done")
    data class ToolDone(val tool: String, val subject: String? = null) : StreamEvent

    /** The turn entered another stage, which lasts until the next one or the end of the turn. */
    @Serializable
    @SerialName("stage")
    data class Stage(val stage: TurnStage) : StreamEvent

    /**
     * What the session has consumed so far, counting the turn that just ended.
     *
     * The running total and not this turn's share: a client that connected halfway would add up
     * a different figure from the events it happened to see.
     */
    @Serializable
    @SerialName("usage")
    data class Usage(val usage: SessionUsageResponse) : StreamEvent

    /** The turn finished. */
    @Serializable
    @SerialName("done")
    data object Done : StreamEvent

    /** The turn ended badly. The interface words [reason] in the person's language. */
    @Serializable
    @SerialName("error")
    data class Failed(val reason: TurnFailureCode = TurnFailureCode.GENERATION_FAILED) : StreamEvent
}

/** Why a turn ended badly, as the stream carries it. */
@Serializable
enum class TurnFailureCode {
    @SerialName("page_cut_short")
    PAGE_CUT_SHORT,

    @SerialName("reply_cut_short")
    REPLY_CUT_SHORT,

    @SerialName("not_finished")
    NOT_FINISHED,

    @SerialName("timed_out")
    TIMED_OUT,

    @SerialName("not_saved")
    NOT_SAVED,

    @SerialName("generation_failed")
    GENERATION_FAILED,
}

/** The stages of a turn the screen names while they last. */
@Serializable
enum class TurnStage {
    @SerialName("planning")
    PLANNING,

    @SerialName("checking")
    CHECKING,

    @SerialName("reviewing")
    REVIEWING,

    @SerialName("correcting")
    CORRECTING,

    @SerialName("saving")
    SAVING,
}
